import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Typography, Space, Spin } from "antd";
import { ExclamationCircleOutlined, LinkOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { types, errorAction } from "@authzsvc/api-lib";
import { getIdentityById } from "../../actions/api";
import { showErrorNotification } from "../../util";
import { sidebar_resources } from "../../config";

const InternalStateWarning = ({ selectedGroup }) => {
  const dispatch = useDispatch();
  const internalState = selectedGroup.internalState;
  const RESOURCES_PORTAL_URL = sidebar_resources
    ? `${sidebar_resources}/required-actions`
    : null;
  const [owner, setOwner] = useState(null);
  const [loadingOwner, setLoadingOwner] = useState(false);

  const internalStateMessages = {
    ActionRequired: null, // Will be handled separately
    Blocked:
      "It is not possible to perform any action on this group because it is blocked.",
    Archived:
      "It is not possible to perform any action on this group because it has been archived. Please contact the service desk if you need to recover the group.",
    Deleted:
      "It is not possible to perform any action on this group because it has been deleted. Please contact the service desk if you need to recover the group.",
  };

  // Fetch owner identity when component mounts or when ownerId changes
  useEffect(() => {
    const fetchOwner = async () => {
      if (internalState === "ActionRequired" && selectedGroup.ownerId) {
        setLoadingOwner(true);
        try {
          const response = await dispatch(
            getIdentityById(selectedGroup.ownerId, {})
          );
          if (response && response.payload) {
            setOwner(response.payload.data);
          } else if (response.type === errorAction(types.GET_IDENTITY_BY_ID)) {
            showErrorNotification(
              response.payload.title,
              response.payload.data.message
            );
          }
        } catch (error) {
          console.error("Error fetching owner identity:", error);
        } finally {
          setLoadingOwner(false);
        }
      }
    };

    fetchOwner();
  }, [internalState, selectedGroup.ownerId, dispatch]);

  const getHeader = () => {
    return internalState === "ActionRequired" ? "Action Required" : "Warning";
  };

  const getActionRequiredMessage = () => {
    const ownerName = owner?.displayName || "the new owner";
    return (
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <span>
          {ownerName ? (
            <>
              <strong>{ownerName}</strong> must complete the ownership
              confirmation by visiting{" "}
            </>
          ) : (
            <>
              The new owner must complete the ownership confirmation by visiting{" "}
            </>
          )}
          {RESOURCES_PORTAL_URL ? (
            <a
              href={RESOURCES_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              the required actions page
              <LinkOutlined style={{ fontSize: "0.85em" }} />
            </a>
          ) : (
            "the required actions page"
          )}
          . If this is not possible please contact the{" "}
          <a href="https://cern.service-now.com/service-portal?id=service_element&name=SSO-Service">
            Service Desk
          </a>
          .
        </span>
      </Space>
    );
  };

  const getMessage = () => {
    if (internalState === "ActionRequired") {
      return (
        <Spin spinning={loadingOwner} size="small">
          {getActionRequiredMessage()}
        </Spin>
      );
    }
    return (
      internalStateMessages[internalState] ||
      "This group is not currently editable. Please contact the service desk."
    );
  };

  return (
    <Alert
      type="error"
      showIcon
      icon={<ExclamationCircleOutlined style={{ marginRight: 16 }} />}
      message={
        <>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {getHeader()}
          </Typography.Title>
          {getMessage()}
        </>
      }
      style={{ marginBottom: 16 }}
    />
  );
};

InternalStateWarning.propTypes = {
  selectedGroup: PropTypes.object.isRequired,
};

export default InternalStateWarning;
